import React, { useEffect, useState } from 'react';
import { AlertTriangle, AlertCircle, CheckCircle } from 'lucide-react';
import { fetchStorageMetrics } from '../../services/storage';

const StorageUsageBar = ({ token, theme = 'light' }) => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadMetrics = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchStorageMetrics(token);
        setMetrics(data);
      } catch (err) {
        setError(err.message || 'Failed to load storage metrics');
        console.error('Storage metrics error:', err);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      loadMetrics();
      // Refresh every 30 seconds
      const interval = setInterval(loadMetrics, 30000);
      return () => clearInterval(interval);
    }
  }, [token]);

  if (loading) {
    return (
      <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'}`}>
        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          Loading storage information...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-red-900/20' : 'bg-red-50'}`}>
        <p className={`text-sm ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
          Error loading storage metrics: {error}
        </p>
      </div>
    );
  }

  if (!metrics) return null;

  // Determine status colors and icon
  const getStatusInfo = () => {
    const status = metrics.status.toLowerCase();
    
    switch (status) {
      case 'healthy':
        return {
          icon: CheckCircle,
          color: 'text-green-600',
          bgColor: theme === 'dark' ? 'bg-green-900/20' : 'bg-green-50',
          borderColor: theme === 'dark' ? 'border-green-800' : 'border-green-200',
        };
      case 'warning':
        return {
          icon: AlertTriangle,
          color: 'text-yellow-600',
          bgColor: theme === 'dark' ? 'bg-yellow-900/20' : 'bg-yellow-50',
          borderColor: theme === 'dark' ? 'border-yellow-800' : 'border-yellow-200',
        };
      case 'critical':
      case 'over limit':
        return {
          icon: AlertCircle,
          color: 'text-red-600',
          bgColor: theme === 'dark' ? 'bg-red-900/20' : 'bg-red-50',
          borderColor: theme === 'dark' ? 'border-red-800' : 'border-red-200',
        };
      default:
        return {
          icon: CheckCircle,
          color: 'text-gray-600',
          bgColor: theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100',
          borderColor: theme === 'dark' ? 'border-gray-700' : 'border-gray-200',
        };
    }
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  // Progress bar uses fixed colors: blue for vector store, purple for data folder
  const vectorstoreSegmentColor = 'bg-blue-600';
  const dataFolderSegmentColor = 'bg-purple-600';

  // Calculate segment widths
  const vectorstoreWidth = (metrics.vectorstore_size / metrics.total_allowed) * 100;
  const dataFolderWidth = (metrics.data_folder_size / metrics.total_allowed) * 100;

  return (
    <div
      className={`p-4 rounded-lg border ${statusInfo.bgColor} ${statusInfo.borderColor}`}
    >
      {/* Header with status */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <StatusIcon size={20} className={statusInfo.color} />
          <div>
            <h3 className={`font-semibold text-sm ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
              Storage Usage
            </h3>
            <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              {metrics.status}
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className={`text-sm font-semibold ${statusInfo.color}`}>
            {metrics.usage_percentage.toFixed(1)}%
          </p>
          <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            of {metrics.total_allowed_formatted}
          </p>
        </div>
      </div>

      {/* Segmented Progress Bar */}
      <div className="mb-4">
        <div className={`h-6 rounded-full overflow-hidden border ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'} flex`}>
          {/* Vector Store Segment */}
          {vectorstoreWidth > 0 && (
            <div
              className={`${vectorstoreSegmentColor} transition-all`}
              style={{ width: `${vectorstoreWidth}%`, minWidth: vectorstoreWidth > 5 ? 'auto' : '0' }}
              title={`Vector Store: ${metrics.vectorstore_size_formatted}`}
            >
              {vectorstoreWidth > 10 && (
                <div className="h-full flex items-center justify-center text-white text-xs font-semibold px-1">
                  VS
                </div>
              )}
            </div>
          )}
          
          {/* Data Folder Segment */}
          {dataFolderWidth > 0 && (
            <div
              className={`${dataFolderSegmentColor} transition-all`}
              style={{ width: `${dataFolderWidth}%`, minWidth: dataFolderWidth > 5 ? 'auto' : '0' }}
              title={`Data Folder: ${metrics.data_folder_size_formatted}`}
            >
              {dataFolderWidth > 10 && (
                <div className="h-full flex items-center justify-center text-white text-xs font-semibold px-1">
                  DF
                </div>
              )}
            </div>
          )}
          
          {/* Remaining Space */}
          {(100 - vectorstoreWidth - dataFolderWidth) > 0 && (
            <div
              className={`${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-300'} transition-all`}
              style={{ width: `${100 - vectorstoreWidth - dataFolderWidth}%` }}
              title={`Free: ${metrics.remaining_formatted}`}
            />
          )}
        </div>
      </div>

      {/* Legend and Details */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-3 h-3 rounded bg-blue-600" />
            <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>Vector Store</span>
          </div>
          <p className={`ml-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            {metrics.vectorstore_size_formatted}
          </p>
          <p className={`ml-5 text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
            ({((metrics.vectorstore_size / metrics.total_allowed) * 100).toFixed(1)}%)
          </p>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-3 h-3 rounded bg-purple-600" />
            <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>Data Folder</span>
          </div>
          <p className={`ml-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            {metrics.data_folder_size_formatted}
          </p>
          <p className={`ml-5 text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
            ({((metrics.data_folder_size / metrics.total_allowed) * 100).toFixed(1)}%)
          </p>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className={`w-3 h-3 rounded ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-400'}`} />
            <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>Free Space</span>
          </div>
          <p className={`ml-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            {metrics.remaining_formatted}
          </p>
          <p className={`ml-5 text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
            ({(100 - metrics.usage_percentage).toFixed(1)}%)
          </p>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>Total Used</span>
          </div>
          <p className={`ml-5 font-semibold ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            {metrics.total_used_formatted}
          </p>
          <p className={`ml-5 text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
            of {metrics.total_allowed_formatted}
          </p>
        </div>
      </div>

      {/* Threshold Information */}
      {/* <div className={`mt-3 pt-3 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-300'}`}>
        <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          Thresholds: Warning at {metrics.warning_threshold}% • Critical at {metrics.critical_threshold}%
        </p>
      </div> */}
    </div>
  );
};

export default StorageUsageBar;
